import React, { useEffect, useState } from 'react'
import {
  ArrowLeft,
  Bell,
  Trash2,
  Clock,
  CheckCircle2,
  Loader2,
  RefreshCcw,
  Search,
} from 'lucide-react'
import {
  collection,
  getDocs,
  deleteDoc,
  query,
  orderBy,
  where,
  limit,
  Timestamp,
  onSnapshot,
} from 'firebase/firestore'
import { db } from '../firebaseConfig'
import { useNavigate } from 'react-router-dom'

export default function CleanOldNotifications() {
  const [status, setStatus] = useState('idle')
  const [log, setLog] = useState([])
  const [deletedCount, setDeletedCount] = useState(0)
  const [days, setDays] = useState(7)
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState([])
  const [search, setSearch] = useState('')

  const navigate = useNavigate()

  // ============================================================
  // 🔹 LOAD NOTIFIKASI TERBARU
  // ============================================================
  useEffect(() => {
    const q = query(
      collection(db, 'notifications'),
      orderBy('timestamp', 'desc'),
      limit(100)
    )

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const list = snapshot.docs.map((doc) => ({
          id: doc.id,
          ref: doc.ref,
          ...doc.data(),
        }))
        setNotifications(list)
        setLoading(false)
      },
      (err) => {
        console.error('⚠️ Gagal memuat notifikasi:', err)
        setLog(['⚠️ ' + err.message])
        setLoading(false)
      }
    )

    return () => unsubscribe()
  }, [])

  // ============================================================
  // 🔹 TOGGLE SELECTION
  // ============================================================
  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }

  const selectAll = () => {
    if (selected.length === notifications.length) setSelected([])
    else setSelected(notifications.map((n) => n.id))
  }

  // ============================================================
  // 🔹 DELETE SELECTED
  // ============================================================
  const handleDeleteSelected = async () => {
    if (selected.length === 0) {
      alert('Pilih minimal satu notifikasi untuk dihapus.')
      return
    }

    const confirmDelete = window.confirm(
      `Yakin ingin menghapus ${selected.length} notifikasi yang dipilih?`
    )
    if (!confirmDelete) return

    setStatus('running')
    setLog([])
    setDeletedCount(0)

    try {
      const toDelete = notifications.filter((n) => selected.includes(n.id))
      await Promise.all(toDelete.map((n) => deleteDoc(n.ref)))

      setDeletedCount(toDelete.length)
      setSelected([])
      setStatus('done')
      setLog([`✅ ${toDelete.length} notifikasi berhasil dihapus.`])
    } catch (err) {
      console.error('Error deleting notifications:', err)
      setStatus('error')
      setLog(['❌ Terjadi kesalahan: ' + err.message])
    }
  }

  // ============================================================
  // 🔹 DELETE OLD NOTIFICATIONS
  // ============================================================
  const handleDeleteOldNotifications = async () => {
    try {
      const confirmDelete = window.confirm(
        `Yakin ingin menghapus semua notifikasi lebih dari ${days} hari?`
      )
      if (!confirmDelete) return

      setStatus('running')
      setLog([])
      setDeletedCount(0)

      const cutoff = Timestamp.fromDate(
        new Date(Date.now() - days * 24 * 60 * 60 * 1000)
      )

      const q = query(
        collection(db, 'notifications'),
        where('timestamp', '<', cutoff)
      )

      const snapshot = await getDocs(q)
      const total = snapshot.docs.length

      if (total === 0) {
        setStatus('done')
        setLog([`Tidak ada notifikasi yang lebih lama dari ${days} hari.`])
        return
      }

      await Promise.all(snapshot.docs.map((docSnap) => deleteDoc(docSnap.ref)))

      setDeletedCount(total)
      setStatus('done')
      setLog([`✅ ${total} notifikasi lama dihapus.`])
    } catch (err) {
      console.error('Error deleting old notifications:', err)
      setStatus('error')
      setLog(['❌ Terjadi kesalahan: ' + err.message])
    }
  }

  // ============================================================
  // 🔍 FILTER SEARCH
  // ============================================================
  const filtered = notifications.filter((n) => {
    const q = search.toLowerCase()
    return (
      n.title?.toLowerCase().includes(q) ||
      n.message?.toLowerCase().includes(q) ||
      n.target?.toLowerCase().includes(q)
    )
  })

  // ============================================================
  // 🔹 RENDER LIST
  // ============================================================
  const renderList = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center py-8 text-gray-500">
          <Loader2 size={18} className="animate-spin mr-2" />
          Memuat notifikasi...
        </div>
      )
    }

    if (filtered.length === 0) {
      return (
        <div className="text-center py-10 text-gray-400">
          Tidak ada notifikasi ditemukan.
        </div>
      )
    }

    return (
      <div className="max-h-[420px] overflow-y-auto border border-gray-200 rounded-lg bg-white">
        <div className="flex items-center justify-between p-2 bg-gray-100 border-b border-gray-200 text-sm font-medium text-gray-700">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={selected.length === notifications.length}
              onChange={selectAll}
            />
            <span>Pilih Semua ({filtered.length})</span>
          </div>
          {selected.length > 0 && (
            <button
              onClick={handleDeleteSelected}
              className="flex items-center gap-1 text-red-600 hover:text-red-700 text-xs font-medium"
            >
              <Trash2 size={14} /> Hapus yang Dipilih
            </button>
          )}
        </div>

        {filtered.map((n) => (
          <div
            key={n.id}
            className="flex items-start gap-3 p-3 border-b last:border-0 border-gray-100 bg-white hover:bg-gray-50 transition"
          >
            <input
              type="checkbox"
              checked={selected.includes(n.id)}
              onChange={() => toggleSelect(n.id)}
              className="mt-1"
            />
            <div className="flex-1">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-gray-800 text-sm flex items-center gap-1">
                  <Bell size={14} /> {n.title || 'Tanpa Judul'}
                </h3>
                <span className="text-xs text-gray-500">
                  {n.timestamp?.toDate
                    ? new Date(n.timestamp.toDate()).toLocaleString()
                    : '-'}
                </span>
              </div>
              <p className="text-gray-700 text-sm mt-1">{n.message}</p>
              {n.target && (
                <p className="text-xs text-gray-500 mt-1">
                  🎯 Target: {n.target}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    )
  }

  // ============================================================
  // 🔹 UI UTAMA
  // ============================================================
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* HEADER */}
      <div className="flex items-center gap-3 mb-5">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-blue-600 hover:text-blue-800 transition"
        >
          <ArrowLeft size={18} /> Kembali
        </button>
        <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
          <Bell size={20} className="text-blue-600" /> Manajemen Notifikasi
        </h2>
      </div>

      {/* SEARCH BAR */}
      <div className="flex items-center gap-2 mb-4">
        <Search size={16} className="text-gray-500" />
        <input
          type="text"
          placeholder="Cari notifikasi berdasarkan judul, pesan, atau target..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <p className="text-gray-600 mb-4 text-sm">
        Kamu dapat melihat, mencari, memilih, dan menghapus notifikasi tertentu,
        atau membersihkan notifikasi lama secara otomatis.
      </p>

      {/* LIST */}
      <div className="mb-8">{renderList()}</div>

      {/* AUTO DELETE */}
      <div className="border-t border-gray-200 pt-5 mt-5">
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2 text-gray-800">
          <Trash2 size={18} className="text-blue-600" /> Hapus Notifikasi Lama
        </h3>

        <div className="flex items-center gap-3 mb-4">
          <label className="text-sm font-medium text-gray-700">
            Hapus notifikasi lebih lama dari:
          </label>
          <input
            type="number"
            value={days}
            min={1}
            onChange={(e) => setDays(Number(e.target.value))}
            className="w-20 px-2 py-1 border rounded-md text-center"
          />
          <span className="text-gray-600 text-sm">hari</span>
        </div>

        <button
          onClick={handleDeleteOldNotifications}
          disabled={status === 'running'}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-white font-medium transition ${
            status === 'running'
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-red-600 hover:bg-red-700'
          }`}
        >
          {status === 'running' ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Menghapus...
            </>
          ) : (
            <>
              <Trash2 size={18} />
              Hapus Notifikasi Lama
            </>
          )}
        </button>

        {/* STATUS */}
        <div className="mt-4">
          {status === 'done' && (
            <div className="flex items-center gap-2 text-green-600 font-medium">
              <CheckCircle2 size={18} />
              {deletedCount} notifikasi dihapus.
            </div>
          )}
          {status === 'error' && (
            <div className="text-red-600 font-medium">
              Terjadi kesalahan saat menghapus notifikasi.
            </div>
          )}
        </div>

        {/* LOG */}
        {log.length > 0 && (
          <div className="mt-5 p-3 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-700 max-h-64 overflow-y-auto">
            {log.map((l, i) => (
              <div key={i}>{l}</div>
            ))}
          </div>
        )}

        {/* FOOTER */}
        <div className="mt-6 flex items-center gap-2 text-xs text-gray-500">
          <Clock size={14} />
          <span>Terakhir dijalankan: {new Date().toLocaleString()}</span>
          <button
            onClick={() => window.location.reload()}
            className="ml-auto flex items-center gap-1 text-blue-500 hover:text-blue-700 transition"
          >
            <RefreshCcw size={14} /> Muat Ulang
          </button>
        </div>
      </div>
    </div>
  )
}
